"use client";

import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import Logo from "@/components/ui/Logo/Logo";
import PinkOctober from "@/components/ui/PinkOctober/PinkOctober";
import { useTranslations } from "next-intl";
import { motion, AnimatePresence } from "motion/react";

export default function LandingHeader() {
    const t = useTranslations();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        // Check initial scroll position
        handleScroll();

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const navLinks = [
        { href: "/courses", label: t("landing_nav_courses") },
        { href: "/reps", label: t("landing_nav_reps") },
        { href: "/accelerators", label: t("landing_nav_accelerators") },
        // Masqué tant que MENTORING_IN_NAVIGATION est à false (next.config.mjs).
        ...(process.env.MENTORING_IN_NAVIGATION
            ? [{ href: "/mentoring", label: t("landing_nav_mentoring") }]
            : []),
        { href: "/pricing", label: t("landing_nav_pricing") },
        { href: "/articles", label: t("landing_nav_articles") },
        { href: "/tools", label: t("landing_nav_tools") },
    ];

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled
                    ? "bg-white/85 dark:bg-bgDark-700/90 backdrop-blur-xl shadow-sm border-b border-gray-200/50 dark:border-grayDark-700/50"
                    : ""
            }`}
        >
            <div className={`transition-all duration-300 ${isScrolled ? "mx-0 mt-0" : "mx-4 sm:mx-6 mt-4"}`}>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between transition-all duration-300 ${
                    isScrolled
                        ? ""
                        : "bg-white/80 dark:bg-bgDark-700/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-grayDark-700/50 shadow-sm"
                }`}>
                    {/* Logo */}
                    <div className="flex items-center gap-1.5 shrink-0">
                        <Link href="/" className="flex items-center shrink-0">
                            <Logo ratio={0.85} />
                        </Link>
                        <PinkOctober />
                    </div>

                    {/* Center Navigation - Desktop */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-grayDark-300 dark:hover:text-white transition-colors px-4 py-2 rounded-lg hover:bg-gray-100/80 dark:hover:bg-grayDark-800/80"
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Right Navigation - Desktop */}
                    <div className="hidden lg:flex items-center gap-2">
                        <Link
                            href="/login"
                            className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-grayDark-300 dark:hover:text-white transition-colors px-4 py-2"
                        >
                            {t("landing_header_login")}
                        </Link>
                        <Link
                            href="/signup"
                            className="btn-landing btn-3d lip-orange text-sm font-medium px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl"
                        >
                            {t("landing_header_signup")}
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-grayDark-800 hover:bg-gray-200 dark:hover:bg-grayDark-700 transition-colors"
                        aria-expanded={mobileMenuOpen}
                        aria-controls="landing-mobile-menu"
                        aria-label={t(
                            mobileMenuOpen
                                ? "landing_header_menu_close"
                                : "landing_header_menu_open",
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-700 dark:text-grayDark-300"
                            aria-hidden="true"
                        >
                            {mobileMenuOpen ? (
                                <>
                                    <path d="M18 6 6 18" />
                                    <path d="m6 6 12 12" />
                                </>
                            ) : (
                                <>
                                    <path d="M4 8h16" />
                                    <path d="M4 16h16" />
                                </>
                            )}
                        </svg>
                    </button>
                </div>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            id="landing-mobile-menu"
                            className="lg:hidden mt-2 bg-white/95 dark:bg-bgDark-700/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-grayDark-700/50 shadow-lg overflow-hidden"
                        >
                            <nav className="p-4 space-y-1">
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-grayDark-300 dark:hover:text-white transition-colors px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-grayDark-800"
                                    >
                                        {link.label}
                                    </Link>
                                ))}
                            </nav>
                            <div className="border-t border-gray-200 dark:border-grayDark-700 p-4 space-y-2">
                                <Link
                                    href="/login"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="block text-center text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-grayDark-300 dark:hover:text-white transition-colors px-4 py-3 rounded-xl hover:bg-gray-100 dark:hover:bg-grayDark-800"
                                >
                                    {t("landing_header_login")}
                                </Link>
                                <Link
                                    href="/signup"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="btn-landing btn-3d lip-orange block text-center text-sm font-medium px-5 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl"
                                >
                                    {t("landing_header_signup")}
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </header>
    );
}
